import React, { useState, useEffect, useContext } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { CPF } from 'gerador-validador-cpf'

import queryString from 'query-string'

import {
  clearMedicalRequest,
  getPlanGroups,
  sendProcedureRequest,
  registerInfoGroup,
} from 'redux/actions'

import { SignupContext } from 'contexts/SignupContext'

import Dropdown from 'components/Dropdown'
import Register from '../Register'
import Row from 'components/Row'
import { Typography, Button, CoreIcons, TextInput, Spinner, Column } from 'components'

import { stepsList } from './Steps'
import { PATIENT } from 'settings/_profileSettings'
import { planGroupsSettings, plansType } from 'settings/_planGroupSettings'
import { history } from 'routers/AppRouter'
import * as routes from 'settings/_routesSettings'
import axios from '../../../settings/axios'
import { apiEndpoints } from 'settings/_apiSettings'
import { CAPITATION_PLANS_GROUPS, CAREPLUS_PLAN_GROUP_CODE } from 'settings/_planGroupCode'

const InfoGroup = () => {

  const {
    setPlanGroup,
    setCpf,
    cpf,
    cpfMsg,
    isCpfValid,
    listPlanGroups,
  } = useContext(SignupContext)

  const dispatch = useDispatch()

  const [isLoadingPlans, setIsLoadingPlans] = useState(true)
  const [isLoadingProcedure, setIsLoadingProcedure] = useState(false)
  const [cardNumberExists, setCardNumberExists] = useState(false)
  const [isReadyToSubmit, setIsReadyToSubmit] = useState(false)
  const [fields, setFields] = useState({
    plan_code: { value: '' },
    card_number: {
      value: '',
      isValid: false
    },
    hasMedicRequest: false,
    medicRequest: {
      isValid: true,
      message: '',
      placeholder: 'Anexar pedido médico',
      value: '',
      displayValue: ''
    }
  })
  const [planGroupType, setPlanGroupType] = useState(plansType.PARTICULAR)
  const [groupPlan, setGroupPlan] = useState(null)

  const groupPlans = useSelector(state => state.plans.groupPlans)
  const procedureError = useSelector(state => state.medicalRequests.errorMessage)

  const settings = planGroupsSettings[groupPlans.code]
  const plansPlaceholder = settings && settings.plansPlaceholder ? settings.plansPlaceholder : 'Tipo de plano'

  const showCpfInput = [plansType.CORPORATE, plansType.CAPITATION].includes(planGroupType) || settings?.useCpfInCardNumber

  useEffect(() => {
    const groupPlan = queryString.parse(window.location.search).group_plan

    async function setSelectedPlanGroup(code) {
      const planGroups = await listPlanGroups()
      const planGroup = planGroups.find(planGroup => planGroup.code === code)

      setPlanGroup({
        code: planGroup.code,
        name: planGroup.name,
        category: planGroup.category,
        use_atena: planGroup.use_atena
      })
    }

    if (groupPlan) {
      setGroupPlan(groupPlan)
      setSelectedPlanGroup(groupPlan)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    function checkFields() {
      let isReadyToSubmit = false

      if (showCpfInput) {
        isReadyToSubmit = isCpfValid
      } else {
        isReadyToSubmit = fields.card_number.isValid
        if (planGroupType === plansType.CAREPLUS) {
          isReadyToSubmit = fields.card_number.isValid
        }
      }

      setIsReadyToSubmit(fields.plan_code.value !== '' && isReadyToSubmit)
    }

    checkFields()
    dispatch(clearMedicalRequest())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, cpf, isCpfValid])

  useEffect(() => {
    function selectFlow() {
      const isGroupPlanDefined = groupPlan !== '' && groupPlan !== null

      if (isGroupPlanDefined && !planGroupType !== plansType.PARTICULAR) {
        dispatch(getPlanGroups(groupPlan, { plan_profile_role: PATIENT }))
      }
    }

    selectFlow()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupPlan])

  useEffect(() => {
    if (Object.keys(groupPlans).length <= 0) return

    setIsLoadingPlans(false)

    const newPlanGroupType = getCurrentPlanGroupType()

    setPlanGroupType(newPlanGroupType)
  }, [groupPlans])

  function getCurrentPlanGroupType() {
    if (groupPlans.use_atena) return plansType.CORPORATE
    if (groupPlans.code === CAREPLUS_PLAN_GROUP_CODE) return plansType.CAREPLUS
    if (CAPITATION_PLANS_GROUPS.includes(groupPlans.code)) return plansType.CAPITATION

    return plansType.ENTERPRISE
  }

  function formatPlansDropdown() {
    if (groupPlans && groupPlans.plans) {
      return groupPlans.plans.map(plan => {
        return {
          code: plan.id,
          name: plan.name
        }
      })
    }

    return []
  }

  function handlePlanSelection(code) {
    setFields(prevState => ({
      ...prevState,
      plan_code: {
        ...prevState.plan_code,
        value: code,
      }
    }))
  }

  function handleCardNumber(e, mask) {
    const inputValue = e.target.value
    const inputCleanedValue = inputValue.replace(/_+/g, '').replace(/\.+/g, '')
    const minLength = mask && mask.match(/1/g)?.length

    setFields(prevState => ({
      ...prevState,
      card_number: {
        ...prevState.card_number,
        isValid: mask ? inputCleanedValue.length === minLength : inputCleanedValue.length >= 1,
        value: inputCleanedValue,
      }
    }))
  }

  async function handleProcedureRequest() {
    setIsLoadingProcedure(true)

    const { errorMessage, startDate, planPassword } = await dispatch(sendProcedureRequest(
      fields.card_number.value,
      CPF.format(cpf, 'digits'),
      groupPlans.plans.find(plan => plan.id === fields.plan_code.value).code
    ))

    setIsLoadingProcedure(false)
    return {
      errorMessage,
      startDate,
      planPassword,
    }
  }

  async function handleCheckCardNumberExists() {
    try {
      setIsLoadingProcedure(true)

      const cardNumber = settings.useCpfInCardNumber ? CPF.format(cpf, 'digits') : fields.card_number.value

      const url = apiEndpoints.PERSON_PROFILE_BY_CARD(cardNumber, groupPlans.code)
      await axios.head(url)

      setIsLoadingProcedure(false)

      return {
        cardNumberExists: true
      }
    } catch (err) {
      setIsLoadingProcedure(false)
      if (err.response.status === 404) {
        setIsLoadingProcedure(false)
        return {
          cardNumberExists: false
        }
      }
    }
  }

  async function handleSubmit() {

    dispatch(clearMedicalRequest())

    let formattedData = {
      partner_plan_id: fields.plan_code.value,
      plan_validity: {}
    }

    if (planGroupType === plansType.CAREPLUS || planGroupType === plansType.ENTERPRISE) {

      formattedData = {
        ...formattedData,
        card_number: settings.useCpfInCardNumber ? CPF.format(cpf, 'digits') : fields.card_number.value
      }

      const { cardNumberExists } = await handleCheckCardNumberExists()

      if (cardNumberExists) {
        setCardNumberExists(true)
        return
      }
    }

    if ([plansType.CORPORATE, plansType.CAPITATION].includes(planGroupType)) {
      const { errorMessage } = await handleProcedureRequest()

      if (errorMessage)
        return

      formattedData = {
        ...formattedData,
        cpf: CPF.format(cpf, 'digits'),
      }
    }

    let stepData = {
      plans: [formattedData],
      hasPlanCode: fields.plan_code.value !== ''
    }

    await dispatch(registerInfoGroup(stepData))
    history.push(routes.REGISTER_INFO_PERSONAL)
  }

  function onPrevStep() {
    setCpf(null)
    history.goBack()
  }


  return (
    <Register
      showStepper={true}
      showLoginInfo={false}
      currentStep={stepsList.INFO_GROUP}
    >
      {(isLoadingPlans || !planGroupType)
        ? (<Spinner height='30vh' />)
        : (
          <div className='Register__InfoGroup'>
            <div className='Register__InfoGroup__Head'>
              <div className='Register__InfoGroup__Head__Image'>
                <img
                  src={groupPlans.logo_url}
                  alt={`${groupPlans.code}-${groupPlans.name}`}
                  title={groupPlans.name}
                />
              </div>

              <div className='Register__InfoGroup__Head__Text'>
                <Typography align='left'>Insira os dados do seu</Typography>
                <Typography align='left'>
                  {' '}
                  convênio <b>{groupPlans.name}:</b>{' '}
                </Typography>
              </div>
            </div>

            <div className='Register__InfoGroup__Forms'>
              <div className='Register__InfoGroup__Forms__Item'>
                <Dropdown
                  name='plan'
                  options={formatPlansDropdown()}
                  onSelect={code => handlePlanSelection(code)}
                  placeholder={plansPlaceholder}
                  dropDownOverflow
                  dropDownItensToShow='3'
                  dataCy='signup-plan-type'
                  optionsListCustomClassName='Register__InfoGroup__Forms__Dropdown'
                />
              </div>
              {showCpfInput ? (
                <div className='Register__InfoGroup__Forms__Item'>
                  <TextInput
                    placeholder='CPF*'
                    name='cpf'
                    hasError={cpfMsg}
                    inputHeight='4.8rem'
                    mask='111.111.111-**'
                    messageError={cpfMsg}
                    onInputChange={e => setCpf(e.target.value)}
                    value={cpf}
                  />
                </div>
              ) : (
                <div className='Register__InfoGroup__Forms__Item'>
                  <TextInput
                    name='card_number'
                    placeholder={settings?.identifier.placeholder}
                    mask={settings?.identifier.mask ? settings?.identifier.mask : false}
                    value={fields.card_number.value}
                    onInputChange={e => handleCardNumber(e, settings?.identifier.mask)}
                    data-cy='signup-card-number'
                  />
                </div>
              )}

              {isLoadingProcedure && <Spinner height='5rem' />}
              {(procedureError || cardNumberExists) && (
                <div className='Register__InfoGroup__Error'>
                  <div className='Register__InfoGroup__Error__Icon'>
                    <CoreIcons
                      fill='#FF4949'
                      name='Info'
                      width='22px'
                      height='22px'
                      viewBox='00 00 22 22'
                    />
                  </div>
                  <div className='Register__InfoGroup__Error__Text'>
                    <Typography color='false'>
                      {procedureError}
                      {cardNumberExists && 'Carteirinha já cadastrada'}
                    </Typography>
                  </div>
                </div>
              )}
            </div>

            {!isLoadingProcedure && (
              <Row align='space-between' direction='row' margin='10px 0 0' wrap='nowrap'>
                <Column colWidth={6} rightGutter>
                  <Button
                    text='Voltar'
                    classModifier='reverse'
                    onButtonClick={onPrevStep}
                  />
                </Column>

                <Column colWidth={6}>
                  <Button
                    disabled={!isReadyToSubmit}
                    id='submit'
                    type='submit'
                    text='Próximo'
                    onButtonClick={handleSubmit}
                    data-cy='signup-next-step'
                  />
                </Column>
              </Row>
            )}
          </div>
        )}
    </Register>
  )
}

export default InfoGroup
